import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { FormBuilder, Validators } from "@angular/forms";
import { UserDataServicesService } from "../services/user-data-services.service";
import { deleteDialog, successDialog, errorDialog, confirmRoleChangeDialog } from "src/app/shared/dialog";
import { RoleService } from "../../roles/services/role.service";
import Swal from "sweetalert2";

interface Role {
  roleId: string;
  name: string;
}

@Component({
  selector: "app-user-form",
  templateUrl: "./user-form.component.html",
  styleUrls: ["./user-form.component.css"]
})
export class UserFormComponent implements OnInit {

  mode: "create" | "view" | "edit" = "create";
  userId = "";
  authUserId = "";
  creationDate = "";
  projectId: string | null = null;
  currentRoleName = "";
  originalRoleId: string | null = null;

  roles: Role[] = [];
  showPassword = false;

  userForm = this.fb.group({
    email: ["", [Validators.required, Validators.email]],
    name: ["", Validators.required],
    password: ["", Validators.required],
    role: ["", Validators.required]
  });

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private userService: UserDataServicesService,
    private roleService: RoleService
  ) { }

  // -------------------------------------------------------
  // INIT
  // -------------------------------------------------------
  ngOnInit(): void {
    const currentUrl = this.router.url;

    if (currentUrl.includes("create-user")) this.mode = "create";
    if (currentUrl.includes("user-details")) this.mode = "view";
    if (currentUrl.includes("user-edit")) this.mode = "edit";

    this.userId = this.route.snapshot.paramMap.get("id") || "";

    // 🔥 NEW — detect projectId
    this.projectId = this.route.snapshot.paramMap.get("projectId");

    this.loadRoles();

    if (this.mode !== "create") {
      this.loadUser();
    }
  }


  // -------------------------------------------------------
  // LOAD ROLES
  // -------------------------------------------------------
  loadRoles() {
    this.roleService.getRolesForProject(this.projectId!).subscribe({
      next: (res: any) => {
        this.roles = res.content || res;
      },
      error: () => console.error("Failed to load project roles")
    });
  }

  // -------------------------------------------------------
  // LOAD USER DETAILS
  // -------------------------------------------------------
  loadUser() {
    this.userService.getUserById(this.userId).subscribe({
      next: (u: any) => {
        this.userForm.patchValue({
          email: u.email,
          name: u.name,
          password: "",
          role: u.role?.roleId
        });

        this.authUserId = u.authUserId;
        this.creationDate = u.creationDate;
        this.originalRoleId = u.role?.roleId;
        this.currentRoleName = u.role?.name;

        // 🔒 EDIT MODE → lock email & name
        if (this.mode === 'edit') {
          this.userForm.get('email')?.disable();
          this.userForm.get('name')?.disable();
          this.userForm.get('password')?.disable();
        }

        // VIEW MODE → lock everything (already correct)
        if (this.mode === 'view') {
          setTimeout(() => {
            document.querySelectorAll("input, textarea, select")
              .forEach((el: any) => el.disabled = true);
          });
        }
      }
    });
  }




  // -------------------------------------------------------
  // CREATE USER
  // -------------------------------------------------------
  createUser() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const payload = {
      email: this.userForm.value.email!,
      name: this.userForm.value.name!,
      password: this.userForm.value.password!,
      roleId: this.userForm.value.role!,
      projectId: this.projectId
    };

    this.userService.createUser(this.projectId!, payload).subscribe({
      next: () => {
        successDialog("User created successfully!");
        this.router.navigate([`/project-users/${this.projectId}`]);
      },
      error: (err) => {
        const backendCode = this.extractErrorMessage(err);
        const uiMessage = this.mapErrorMessage(backendCode);
        errorDialog(uiMessage);
      }
    });
  }




  // -------------------------------------------------------
  // UPDATE USER (Details + Role)
  // -------------------------------------------------------
  onRoleSelectionChange() {
    const selectedRoleId = this.userForm.value.role;
    console.log("selectedRoleId = "  + selectedRoleId);
    
    // No change → do nothing
    if (!selectedRoleId || selectedRoleId === this.originalRoleId) {
      return;
    }

    const newRoleName =
      this.roles.find(r => r.roleId === selectedRoleId)?.name;

    confirmRoleChangeDialog(this.currentRoleName, newRoleName)
      .then(result => {

        // ❌ User cancelled → revert role
        if (!result.isConfirmed) {
          this.userForm.patchValue({ role: this.originalRoleId });
          return;
        }

        // ✅ User confirmed → call backend
        const payload = {
          authUserId: this.authUserId,
          newRoleId: selectedRoleId
        };

        this.userService.changeUserRole(this.projectId!, payload).subscribe({
          next: () => {
            successDialog("User role updated successfully!");

            // update baseline role
            this.originalRoleId = selectedRoleId;
            this.currentRoleName = newRoleName || "";

          },
          error: () => {
            errorDialog("Failed to update user role");
            // rollback on error
            this.userForm.patchValue({ role: this.originalRoleId });
          }
        });
      });
  }




  // -------------------------------------------------------
  // DELETE USER
  // -------------------------------------------------------
  deleteUser() {
    deleteDialog().then(result => {
      if (result.isConfirmed) {
        this.userService.deleteUser(this.userId).subscribe({
          next: () => {
            successDialog("User deleted!");
            this.router.navigate([`/project-users/${this.projectId}`]);
          },
          error: () => errorDialog("Failed to delete user")
        });
      }
    });
  }



  // -------------------------------------------------------
  // NAVIGATION
  // -------------------------------------------------------
  goToEdit() {
    this.router.navigate([`/project-users/${this.projectId}/user-edit/${this.userId}`]);
  }

  viewUser() {
    this.router.navigate([`/project-users/${this.projectId}/user-details/${this.userId}`]);
  }

  async openSystemChangePasswordDialog() {
    const result = await Swal.fire({
      title: "Set New Password",
      html: `
        <input id="newPassword" class="swal2-input" type="password" placeholder="New password">
        <input id="confirmPassword" class="swal2-input" type="password" placeholder="Confirm new password">
      `,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Update Password",
      cancelButtonText: "Cancel",
      buttonsStyling: false,
      reverseButtons: true,
      customClass: {
        popup: "card",
        confirmButton: "btn-primary",
        cancelButton: "btn-secondary",
      },
      preConfirm: () => {
        const newPassword = (document.getElementById("newPassword") as HTMLInputElement)?.value || "";
        const confirmPassword = (document.getElementById("confirmPassword") as HTMLInputElement)?.value || "";

        if (!newPassword || !confirmPassword) {
          Swal.showValidationMessage("Both password fields are required.");
          return false;
        }

        if (newPassword !== confirmPassword) {
          Swal.showValidationMessage("Passwords do not match.");
          return false;
        }

        if (newPassword.length < 8) {
          Swal.showValidationMessage("Password must be at least 8 characters.");
          return false;
        }

        const hasLetter = /[A-Za-z]/.test(newPassword);
        const hasDigit = /\d/.test(newPassword);
        if (!hasLetter || !hasDigit) {
          Swal.showValidationMessage("Password must contain letters and numbers.");
          return false;
        }

        return { newPassword };
      },
    });

    if (!result.isConfirmed || !result.value?.newPassword) {
      return;
    }

    this.userService
      .changeSystemUserPassword(this.userId, { newPassword: result.value.newPassword })
      .subscribe({
        next: () => {
          successDialog("User password updated successfully!");
        },
        error: (err) => {
          const backendCode = this.extractErrorMessage(err);
          const uiMessage = this.mapErrorMessage(backendCode);
          errorDialog(uiMessage);
        },
      });
  }


  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  goBack() {
    this.router.navigate([`/project-users/${this.projectId}`]);
  }

  private extractErrorMessage(error: any): string {
    if (error?.error?.message) {
      return error.error.message;
    }
    return "UNEXPECTED_ERROR";
  }

  private mapErrorMessage(code: string): string {
    switch (code) {
      case "PASSWORD_REQUIRED":
        return "Password is required.";

      case "PASSWORD_TOO_SHORT":
        return "Password must be at least 8 characters.";

      case "PASSWORD_WEAK":
        return "Password must contain letters and numbers.";

      case "EMAIL_ALREADY_EXISTS":
        return "This email already exists in the project.";

      case "CANNOT_ASSIGN_EQUAL_OR_HIGHER_ROLE":
        return "You cannot assign this role.";

      default:
        return "Something went wrong. Please try again.";
    }
  }



}
